import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    Plus, 
    FolderOpen, 
    Calendar,
    Users,
    Clock,
    Loader2,
    ChevronRight,
    DollarSign,
    Trash2,
    XCircle,
    CheckCircle,
    Search,
    Filter
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';

export default function PosterDashboardPage() {
    const { token, user, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, project: null });
    const [deleting, setDeleting] = useState(false);
    const [statusDialog, setStatusDialog] = useState({ open: false, project: null, newStatus: null });
    const [updatingStatus, setUpdatingStatus] = useState(false);
    
    // Search and filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const response = await fetch(`${API}/poster/projects`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setProjects(data);
            }
        } catch (error) {
            toast.error('Error loading projects');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteProject = async () => {
        if (!deleteDialog.project) return;
        
        setDeleting(true);
        try {
            const response = await fetch(`${API}/poster/projects/${deleteDialog.project.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                toast.success('Project deleted successfully');
                if (data.had_submissions) {
                    toast.info(`Note: ${data.submission_count} submission(s) were associated with this project`);
                }
                fetchProjects();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to delete project');
            }
        } catch (error) {
            toast.error('Error deleting project');
        } finally {
            setDeleting(false);
            setDeleteDialog({ open: false, project: null });
        }
    };

    const toggleProjectStatus = async () => {
        if (!statusDialog.project || !statusDialog.newStatus) return;
        
        setUpdatingStatus(true);
        try {
            const response = await fetch(`${API}/poster/projects/${statusDialog.project.id}`, {
                method: 'PUT',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: statusDialog.newStatus })
            });
            
            if (response.ok) {
                // Update local state
                setProjects(prev => prev.map(p => 
                    p.id === statusDialog.project.id 
                        ? { ...p, status: statusDialog.newStatus }
                        : p
                ));
                toast.success(statusDialog.newStatus === 'closed' 
                    ? 'Project closed - artists can no longer submit' 
                    : 'Project reopened - artists can now submit'
                );
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to update project status');
            }
        } catch (error) {
            toast.error('Error updating project status');
        } finally {
            setUpdatingStatus(false);
            setStatusDialog({ open: false, project: null, newStatus: null });
        }
    };

    const formatDate = (isoString) => {
        return new Date(isoString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const isDeadlineSoon = (deadline) => {
        const deadlineDate = new Date(deadline);
        const now = new Date();
        const daysUntil = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
        return daysUntil <= 7 && daysUntil > 0;
    };

    const isDeadlinePassed = (deadline) => {
        return new Date(deadline) < new Date();
    };

    // Get unique categories from projects
    const categories = [...new Set(projects.map(p => p.category).filter(Boolean))];

    // Filter projects based on search and filters
    const filteredProjects = projects.filter(project => {
        const matchesSearch = searchQuery === '' || 
            project.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            project.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            project.poster_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            project.poster_company?.toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
        const matchesCategory = categoryFilter === 'all' || project.category === categoryFilter;
        
        return matchesSearch && matchesStatus && matchesCategory;
    });

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-[#F51E1E]" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-white">
                            {isAdmin ? 'All Poster Projects' : 'My Projects'}
                        </h1>
                        <p className="text-[#A3A3A3] mt-1">
                            {isAdmin ? 'Manage all poster sync opportunities' : 'Manage your sync opportunities'}
                        </p>
                    </div>
                    {!isAdmin && (
                        <Button
                            onClick={() => navigate('/poster/projects/new')}
                            className="bg-[#F51E1E] hover:bg-[#D41212] text-white"
                            data-testid="create-project-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Project
                        </Button>
                    )}
                </div>

                {/* Search and Filter Bar */}
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                        <Input
                            placeholder="Search projects by title, description, or poster..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 bg-[#262626] border-[#404040] text-white"
                            data-testid="project-search-input"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-full sm:w-[150px] bg-[#262626] border-[#404040] text-white" data-testid="status-filter">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#262626] border-[#404040]">
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger className="w-full sm:w-[180px] bg-[#262626] border-[#404040] text-white" data-testid="category-filter">
                            <Filter className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#262626] border-[#404040]">
                            <SelectItem value="all">All Categories</SelectItem>
                            {categories.map(cat => (
                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Results count */}
                {(searchQuery || statusFilter !== 'all' || categoryFilter !== 'all') && (
                    <p className="text-sm text-[#A3A3A3]">
                        Showing {filteredProjects.length} of {projects.length} projects
                        {searchQuery && <span> matching "{searchQuery}"</span>}
                    </p>
                )}

                {/* Projects Grid */}
                {filteredProjects.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-[#1A1A1A] border border-[#333] rounded-xl p-12 text-center"
                    >
                        <FolderOpen className="w-12 h-12 text-[#666] mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-white mb-2">
                            {projects.length === 0 ? 'No Projects Yet' : 'No projects match your search'}
                        </h3>
                        <p className="text-[#A3A3A3] mb-6">
                            {projects.length === 0 
                                ? 'Create your first project to start receiving submissions'
                                : 'Try adjusting your search or filter criteria'
                            }
                        </p>
                        {projects.length === 0 ? (
                            <Button
                                onClick={() => navigate('/poster/projects/new')}
                                className="bg-[#F51E1E] hover:bg-[#D41212] text-white"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Create Your First Project
                            </Button>
                        ) : (
                            <Button
                                variant="outline"
                                onClick={() => { setSearchQuery(''); setStatusFilter('all'); setCategoryFilter('all'); }}
                                className="border-[#404040] text-white hover:bg-[#404040]"
                            >
                                Clear Filters
                            </Button>
                        )}
                    </motion.div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {filteredProjects.map((project, idx) => (
                            <motion.div
                                key={project.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.05 }}
                                className="relative"
                            >
                                <Link
                                    to={`/poster/projects/${project.id}`}
                                    className="block bg-[#1A1A1A] border border-[#333] rounded-xl p-5 hover:border-[#F51E1E]/50 transition-colors group"
                                    data-testid={`project-card-${project.id}`}
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <Badge className={`
                                            ${project.status === 'open' 
                                                ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                                : 'bg-[#404040] text-[#888] border-[#555]'
                                            }
                                        `}>
                                            {project.status}
                                        </Badge>
                                        <div className="flex items-center gap-1">
                                            {/* Close/Reopen button */}
                                            {project.status === 'open' ? (
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setStatusDialog({ open: true, project, newStatus: 'closed' });
                                                    }}
                                                    data-testid={`close-project-${project.id}`}
                                                    className="p-1.5 rounded-lg text-[#666] hover:text-amber-500 hover:bg-amber-500/10 transition-colors"
                                                    title="Close project"
                                                >
                                                    <XCircle size={16} />
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setStatusDialog({ open: true, project, newStatus: 'open' });
                                                    }}
                                                    data-testid={`reopen-project-${project.id}`}
                                                    className="p-1.5 rounded-lg text-[#666] hover:text-green-500 hover:bg-green-500/10 transition-colors"
                                                    title="Reopen project"
                                                >
                                                    <CheckCircle size={16} />
                                                </button>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setDeleteDialog({ open: true, project });
                                                }}
                                                data-testid={`delete-project-${project.id}`}
                                                className="p-1.5 rounded-lg text-[#666] hover:text-red-500 hover:bg-red-500/10 transition-colors"
                                                title="Delete project"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                            <ChevronRight className="w-5 h-5 text-[#666] group-hover:text-[#F51E1E] transition-colors" />
                                        </div>
                                    </div>

                                    <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2 group-hover:text-[#F51E1E] transition-colors">
                                        {project.title}
                                    </h3>

                                    {/* Show poster name for admins */}
                                    {isAdmin && project.poster_name && (
                                        <p className="text-xs text-[#F51E1E] mb-2">
                                            by {project.poster_name}
                                            {project.poster_company && ` • ${project.poster_company}`}
                                        </p>
                                    )}

                                    <p className="text-sm text-[#A3A3A3] line-clamp-2 mb-4">
                                        {project.description}
                                    </p>

                                    <div className="flex flex-wrap gap-1 mb-4">
                                        {project.genres?.slice(0, 3).map((genre, i) => (
                                            <span 
                                                key={i}
                                                className="text-xs px-2 py-1 bg-[#262626] rounded-full text-[#888]"
                                            >
                                                {genre}
                                            </span>
                                        ))}
                                        {project.genres?.length > 3 && (
                                            <span className="text-xs px-2 py-1 bg-[#262626] rounded-full text-[#888]">
                                                +{project.genres.length - 3}
                                            </span>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#333]">
                                        <div className="flex items-center gap-2">
                                            <Users className="w-4 h-4 text-[#666]" />
                                            <span className="text-sm text-white font-medium">
                                                {project.submission_count || 0}
                                            </span>
                                            <span className="text-xs text-[#666]">submissions</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-[#666]" />
                                            <span className={`text-sm ${
                                                isDeadlinePassed(project.deadline) 
                                                    ? 'text-red-400' 
                                                    : isDeadlineSoon(project.deadline)
                                                        ? 'text-amber-400'
                                                        : 'text-white'
                                            }`}>
                                                {formatDate(project.deadline)}
                                            </span>
                                        </div>
                                    </div>

                                    {project.budget && (
                                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#333]">
                                            <DollarSign className="w-4 h-4 text-green-400" />
                                            <span className="text-sm text-green-400 font-medium">
                                                ${project.budget.toLocaleString()}
                                            </span>
                                            <span className="text-xs text-[#666]">budget</span>
                                        </div>
                                    )}
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Delete Confirmation Dialog */}
                <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, project: null })}>
                    <AlertDialogContent className="bg-[#1A1A1A] border-[#333]">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">Delete Project</AlertDialogTitle>
                            <AlertDialogDescription className="text-[#A3A3A3]">
                                Are you sure you want to delete "<span className="text-white">{deleteDialog.project?.title}</span>"?
                                This action cannot be undone. Any existing submissions will remain in the system but will no longer be linked to this project.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel className="bg-[#262626] border-[#404040] text-white hover:bg-[#333]">
                                Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleDeleteProject}
                                disabled={deleting}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Close/Reopen Confirmation Dialog */}
                <AlertDialog open={statusDialog.open} onOpenChange={(open) => !open && setStatusDialog({ open: false, project: null, newStatus: null })}>
                    <AlertDialogContent className="bg-[#1A1A1A] border-[#333]">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">
                                {statusDialog.newStatus === 'closed' ? 'Close Project' : 'Reopen Project'}
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-[#A3A3A3]">
                                {statusDialog.newStatus === 'closed' ? (
                                    <>
                                        Are you sure you want to close "<span className="text-white">{statusDialog.project?.title}</span>"?
                                        <br /><br />
                                        Artists will no longer be able to submit tracks. 
                                        All existing submissions will be preserved. You can reopen anytime.
                                    </>
                                ) : (
                                    <>
                                        Are you sure you want to reopen "<span className="text-white">{statusDialog.project?.title}</span>"?
                                        <br /><br />
                                        Artists will be able to submit tracks again.
                                    </>
                                )}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel className="bg-[#262626] border-[#404040] text-white hover:bg-[#333]">
                                Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={toggleProjectStatus}
                                disabled={updatingStatus}
                                className={statusDialog.newStatus === 'closed' 
                                    ? "bg-amber-600 hover:bg-amber-700 text-white"
                                    : "bg-green-600 hover:bg-green-700 text-white"
                                }
                            >
                                {updatingStatus ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    statusDialog.newStatus === 'closed' ? 'Close Project' : 'Reopen Project'
                                )}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </Layout>
    );
}
